/**
 * What every Inertia visit needs before it leaves the browser, applied in
 * `main.js`'s `router.on('before', ...)` hook — pulled into its own module
 * so it is importable (and testable) without triggering `main.js`'s
 * top-level `createInertiaApp(...)` call.
 */

import { resolveCsrfToken } from '@/utils/csrf'

/**
 * Add the CSRF token to a non-GET visit — the `X-XSRF-TOKEN` header always,
 * and the `_token` body field Inertia's own convention expects for a
 * non-Laravel backend (`csrf_protection.py` names it "Inertia.js standard").
 *
 * Reads the token at submit time, cookie first — the cookie is the half the
 * server compares against, and it is refreshed on every page render. Reading
 * it here rather than from `#app[data-page]` is what makes this work after
 * an SPA navigation, since Inertia never updates that attribute.
 */
export function attachCsrfToken(visit) {
  const csrfToken = resolveCsrfToken()
  if (!csrfToken) return visit

  if (!visit.headers) {
    visit.headers = {}
  }
  visit.headers['X-XSRF-TOKEN'] = csrfToken

  const method = visit.method?.toUpperCase()
  if (method && method !== 'GET' && method !== 'HEAD') {
    if (!visit.data) {
      visit.data = {}
    }
    if (typeof visit.data === 'object' && !visit.data._token) {
      visit.data._token = csrfToken
    }
  }

  return visit
}

/**
 * Make sure a partial reload can still see a validation refusal.
 *
 * `RequestValidationError` never populates `flash` — Inertia's own handler
 * (`inertia_request_validation_exception_handler`) shares validation
 * failures as the SEPARATE `page.props.errors` prop, and fastapi-inertia's
 * partial-render filter (`inertia.py::_build_props`) drops any prop not
 * named in `X-Inertia-Partial-Data`, which is built from this exact `only`
 * array. Without `errors` here, `AppLayout.vue`'s errors watcher has
 * nothing to watch on a partial reload, however correct the watcher itself
 * is — found live, 2026-09-25 (Schedule Campaign's 303 that logged nothing
 * and showed nothing), then again independently on eight more pages.
 *
 * Only touches a visit that already has a non-empty `only` — a full reload
 * gets `errors` (and everything else) regardless, and appending to an EMPTY
 * `only` would turn a full reload into an accidental partial one.
 *
 * There is no library-level "always include this prop" hook in
 * `fastapi-inertia` 2.0.12 (`_build_props` filters unconditionally) and no
 * client-side fallback (`@inertiajs/core`'s `Response.process()` reads
 * `page.props.errors` from whatever the server actually sent, nothing
 * more) — so this is the one place in the whole app that can guarantee it,
 * short of a fragile monkey-patch of the vendored library's internals.
 */
export function ensureErrorsSurviveAPartialReload(visit) {
  if (Array.isArray(visit.only) && visit.only.length > 0 && !visit.only.includes('errors')) {
    visit.only = [...visit.only, 'errors']
  }
  return visit
}

/** Everything above, applied to one visit — what `main.js` calls. */
export function applyGlobalVisitDefaults(visit) {
  attachCsrfToken(visit)
  ensureErrorsSurviveAPartialReload(visit)
  return visit
}
